import React, { useState, useRef } from 'react';
import Autosuggest, { InputProps } from 'react-autosuggest';
import { searchProfileMembers } from '@/services/profiles';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import style from './Autocomplete.module.scss';
import { ProfileMember } from '@/types';
import { useSelector } from 'react-redux';
import { IState } from '@/state/IState';
import { UserIcon } from '@/components/Icons';

const renderSuggestion = (suggestion: ProfileMember) => (
    <div className={style.suggestionContent}>
        <div className={style.suggestionAvatar}>
            <UserIcon userId={suggestion.applicationUserId} />
        </div>
        <div className={style.suggestionText}>
            <div>{suggestion.displayName}</div>
            <small>{suggestion.email}</small>
        </div>
    </div>
);

type OwnProps = Omit<InputProps<ProfileMember>, 'onChange' | 'value'> & {
    profileId: number;
    onChange(suggestion: ProfileMember): void;
};

const ProfileUsersAutocomplete: React.FC<OwnProps> = ({ onChange, profileId, ...rest }) => {
    const ownEmail = useSelector((state: IState) => state.global.user!.email);

    const [value, setValue] = useState('');
    const [suggestions, setSuggestions] = useState<ProfileMember[]>([]);
    const activeRequest = useRef<Promise<ProfileMember[]> | null>(null);

    const onSuggestionsFetchRequested = ({ value }) => {
        activeRequest.current = searchProfileMembers(profileId, value);

        activeRequest.current.then((result) => {
            setSuggestions(result);
        });
    };

    const onSuggestionSelected = (event, { suggestion, suggestionValue, suggestionIndex, sectionIndex, method }) => {
        triggerHandler(onChange, suggestion);
    };

    const inputProps: InputProps<ProfileMember> = {
        ...rest,
        placeholder: 'Start typing...',
        value,
        onChange: (event, { newValue }) => setValue(newValue),
    };

    const suggestionsWithoutYourself = suggestions.filter((s) => s.email !== ownEmail);
    return (
        <Autosuggest
            theme={style}
            suggestions={suggestionsWithoutYourself}
            onSuggestionsFetchRequested={onSuggestionsFetchRequested}
            onSuggestionsClearRequested={() => setSuggestions([])}
            onSuggestionSelected={onSuggestionSelected}
            getSuggestionValue={(suggestion) => suggestion.displayName}
            renderSuggestion={renderSuggestion}
            inputProps={inputProps}
        />
    );
};

export default ProfileUsersAutocomplete;